import LayersSummary from "./LayersSummary";
import {
  CheckRounded as BindIcon,
  CloseRounded as DeclineIcon,
  CloseRounded as CloseIcon,
  SaveAltRounded as DownloadIcon,
  EditRounded as EditIcon,
  CalendarMonthRounded as InceptionIcon,
} from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Tooltip,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import * as logger from "common/logger";
import FormattedDatePicker from "components/common/FormattedDatePicker";
import IconButton from "components/common/IconButton";
import * as config from "config";
import { useSnackbar } from "notistack";
import { useState } from "react";
import { useSelector } from "react-redux";
import * as bffService from "services/bffService";
import * as docGenService from "services/docGenService";
import * as docGenHooks from "store/hooks/docGenHooks";
import * as userSelectors from "store/selectors/user/userSelectors";
import * as utils from "utils";

const DownloadDocButton = ({ form, docTitle, label }) => {
  const [loading, setLoading] = useState(false);

  const { downloadTemplate } = docGenHooks.useDownloadTemplate({
    layerIndex: 0,
  });

  const {
    data: templates,
    isTemplatesLoading,
  } = docGenService.useListTemplatesQuery();

  const template = templates?.find((template) => template?.title === docTitle);

  const download = async () => {
    setLoading(true);
    try {
      const submission = await utils
        .authenticatedFetch(
          config.endpoints.persistence(
            "get?" +
              new URLSearchParams({
                filename: `${form.submissionId}.json`,
              })
          ),
          {
            method: "get",
            headers: {
              "Content-Type": "application/json",
            },
          }
        )
        .then((response) => response.json())
        .then((submission) =>
          utils.authenticatedFetch(
            config.endpoints.selectors("select-resolved-submission-from-raw"),
            {
              method: "post",
              headers: {
                "Content-Type": "application/json",
              },
              body: JSON.stringify(submission),
            }
          )
        )
        .then((response) => response.json())
        .then((submission) => ({
          ...submission,
          layers: submission.layers.map((layer) => ({
            ...layer,
            grossPremium:
              form?.quote?.premium[layer.id] ??
              (typeof form?.quote?.premium === "number"
                ? form?.quote?.premium
                : null),
          })),
        }));

      await downloadTemplate({
        template,
        overrideResolvedSubmission: submission,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Tooltip title={`Download ${label}`} arrow>
      <LoadingButton
        size={"small"}
        variant={"outlined"}
        disableElevation
        loading={loading}
        onClick={download}
        disabled={!form?.submissionId || isTemplatesLoading}
        color={"inherit"}
        startIcon={<DownloadIcon />}
        sx={{
          background: "white",
          color: "#232d46",
          borderColor: "gainsboro",
          "&:hover": { background: "#feefe6", borderColor: "#fcae83" },
        }}
      >
        {label}
      </LoadingButton>
    </Tooltip>
  );
};

const QuoteActionButton = ({
  form,
  label,
  sx,
  action,
  layerId,
  icon,
  refetch,
  onChange,
  size,
}) => {
  const [actionQuotedForm] = bffService.useActionQuotedFormMutation();
  const [loading, setLoading] = useState(false);
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  const updateLoading = (loading) => {
    setLoading(loading);
    typeof onChange === "function" && onChange(loading);
  };

  const notifyError = (message) => {
    enqueueSnackbar(message, {
      variant: "error",
      autoHideDuration: 5_000,
      action: (messageId) => (
        <LoadingButton
          color={"error"}
          size={"small"}
          onClick={() => closeSnackbar(messageId)}
        >
          {"Dismiss"}
        </LoadingButton>
      ),
    });
  };

  const onClick = async () => {
    updateLoading(true);
    try {
      const response = await actionQuotedForm({
        formId: form?.id,
        action,
        layerId,
      });
      if (response.error) {
        logger.error(`Failed to apply action ${action}`, {
          breadcrumb: { response },
        });
        notifyError(`${label} failed`);
      }
      await refetch();
    } catch (e) {
      logger.exception(e);
      notifyError(`${label} failed`);
    } finally {
      updateLoading(false);
    }
  };

  return (
    <LoadingButton
      size={size ?? "small"}
      variant={"contained"}
      disableElevation
      onClick={onClick}
      loading={loading}
      disabled={!["QUOTED", "REFERRED"].includes(form?.status)}
      sx={sx}
      endIcon={icon}
    >
      {label}
    </LoadingButton>
  );
};

const BindDialog = ({ form, refetch, onClose }) => {
  const theme = useTheme();
  const [activeLayerId, setActiveLayerId] = useState(null);
  const [loading, setLoading] = useState(false);

  return (
    <Dialog open={true} maxWidth={"md"}>
      <DialogContent>
        <Box
          sx={{
            pointerEvents: loading ? "none" : null,
            opacity: loading ? 0.7 : null,
          }}
        >
          <LayersSummary
            data={form?.formData}
            quote={form?.quote}
            onChange={(layer) => setActiveLayerId(layer.id ?? null)}
            selectable
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <QuoteActionButton
          form={form}
          label={"Bind Selected Cover"}
          action={"ACCEPT"}
          sx={{
            background: theme.brand.status.bound,
            paddingLeft: "0.75rem",
            paddingRight: "0.75rem",
            "&:hover": { background: "seagreen" },
          }}
          onChange={(loading) => setLoading(loading)}
          refetch={() => {
            refetch();
            onClose();
          }}
          size={"medium"}
          layerId={activeLayerId}
        />
        <LoadingButton
          onClick={onClose}
          disabled={loading}
          variant={"outlined"}
          color={"inherit"}
          sx={{
            background: "white",
            color: "#232d46",
            borderColor: "gainsboro",
            "&:hover": { background: "#feefe6", borderColor: "#fcae83" },
          }}
        >
          {"Cancel"}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

const AddInceptionDialog = ({ form, refetch, onClose }) => {
  const theme = useTheme();
  const [inception, setInception] = useState(null);
  const [loading, setLoading] = useState(false);
  const [addInception] = bffService.useAddInceptionMutation();

  const handleSubmit = async () => {
    if (!inception) return;
    setLoading(true);
    try {
      const response = await addInception({
        formId: form?.id,
        inception,
      });
      if (response.error) {
        logger.error("Failed to add inception", { breadcrumb: { response } });
      } else {
        await refetch();
        onClose();
      }
    } catch (e) {
      logger.exception(e);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={true} maxWidth={"xs"} fullWidth>
      <DialogTitle>{"Add Inception Date"}</DialogTitle>
      <DialogContent>
        <Box sx={{ paddingTop: 2 }}>
          <FormattedDatePicker
            label={"Inception Date"}
            value={inception}
            onChangeDate={setInception}
            disabled={loading}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <LoadingButton
          onClick={handleSubmit}
          loading={loading}
          disabled={!inception}
          size={"medium"}
          variant={"contained"}
          disableElevation
          sx={{
            background: theme.brand.status.bound,
            paddingLeft: "0.75rem",
            paddingRight: "0.75rem",
            "&:hover": { background: "seagreen" },
          }}
        >
          {"Add Inception"}
        </LoadingButton>
        <LoadingButton
          onClick={onClose}
          disabled={loading}
          variant={"outlined"}
          color={"inherit"}
          sx={{
            background: "white",
            color: "#232d46",
            borderColor: "gainsboro",
            "&:hover": { background: "#feefe6", borderColor: "#fcae83" },
          }}
        >
          {"Cancel"}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

const ActionMenu = ({ form, refetch, onEdit, onClose }) => {
  const theme = useTheme();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [inceptionDialogOpen, setInceptionDialogOpen] = useState(false);
  const isFormsAdmin = useSelector(userSelectors.isFormsAdminUser);
  const showEdit = !["BOUND", "NOT_TAKEN_UP", "DECLINED_TO_QUOTE"].includes(
    form?.status
  );
  const showBindOrDecline =
    form?.status === "QUOTED" || (form?.status === "REFERRED" && isFormsAdmin);
  const showAddInception =
    !form?.formData?.inception &&
    (showBindOrDecline || form?.status === "BOUND");
  const hasMultipleLayers =
    Object.values(form?.quote?.premium ?? {}).length > 1;

  return (
    <Stack direction={"row"} gap={1}>
      {dialogOpen && (
        <BindDialog
          form={form}
          refetch={refetch}
          onClose={() => setDialogOpen(false)}
        />
      )}
      {inceptionDialogOpen && (
        <AddInceptionDialog
          form={form}
          refetch={refetch}
          onClose={() => setInceptionDialogOpen(false)}
        />
      )}
      {showEdit && (
        <IconButton
          icon={EditIcon}
          tooltip={"Edit Submission"}
          onClick={onEdit}
        />
      )}
      {showBindOrDecline && (
        <>
          {hasMultipleLayers ? (
            <LoadingButton
              size={"small"}
              variant={"contained"}
              disableElevation
              onClick={() => setDialogOpen(true)}
              disabled={!["QUOTED", "REFERRED"].includes(form?.status)}
              sx={{
                background: theme.brand.status.bound,
                paddingLeft: "0.75rem",
                paddingRight: "0.75rem",
                "&:hover": { background: "seagreen" },
              }}
              endIcon={<BindIcon />}
            >
              {"Bind"}
            </LoadingButton>
          ) : (
            <QuoteActionButton
              form={form}
              label={"Bind"}
              action={"ACCEPT"}
              icon={<BindIcon />}
              sx={{
                background: theme.brand.status.bound,
                paddingLeft: "0.75rem",
                paddingRight: "0.75rem",
                "&:hover": { background: "seagreen" },
              }}
              refetch={refetch}
            />
          )}
          <QuoteActionButton
            form={form}
            label={"Decline"}
            action={"DECLINE"}
            icon={<DeclineIcon />}
            sx={{
              background: theme.brand.status.notTakenUp,
              paddingLeft: "0.75rem",
              paddingRight: "0.75rem",
              "&:hover": { background: "slateblue" },
            }}
            refetch={refetch}
          />
        </>
      )}
      {showAddInception && (
        <LoadingButton
          size={"small"}
          variant={"outlined"}
          disableElevation
          onClick={() => setInceptionDialogOpen(true)}
          sx={{
            background: "white",
            color: "#232d46",
            borderColor: "gainsboro",
            paddingLeft: "0.75rem",
            paddingRight: "0.75rem",
            "&:hover": { background: "#feefe6", borderColor: "#fcae83" },
          }}
          endIcon={<InceptionIcon />}
        >
          {"Add Inception"}
        </LoadingButton>
      )}
      <DownloadDocButton
        form={form}
        docTitle={
          hasMultipleLayers ? "CMPL Quote (Multiple Options)" : "CMPL Quote"
        }
        label={"Quote"}
      />
      {form?.status === "BOUND" && (
        <>
          <DownloadDocButton
            form={form}
            docTitle={"CMPL Binder"}
            label={"Binder"}
          />
          <DownloadDocButton
            form={form}
            docTitle={"CMPL Policy"}
            label={"Policy"}
          />
        </>
      )}
      <IconButton icon={CloseIcon} tooltip={"Close"} onClick={onClose} />
    </Stack>
  );
};

export default ActionMenu;
